// Filtrado y ordenamiento de la tabla de Clientes del Portal Admin SureKeep — lógica PURA, sin
// React y sin fetch. La fila llega YA cruzada del BFF: no hay una segunda lectura que esperar.
//
// Todo esto corre sobre las filas ACUMULADAS, no sobre el padrón: mientras queden páginas, quien
// pinta tiene que declararlo (ver `creditCustomersLoaded`).
import type { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import type { CreditCustomerListItem } from '@/api/services/loan-credit';
import {
    creditRowStatusOf,
    creditRowStatusSortValue,
    hasActiveCredit,
    isInArrears,
} from './creditCustomerCredit';
import {
    digitsOf,
    LEVEL_ORDER,
    LIST_STATE_NOTE,
    type CustomerCreditStatus,
} from './creditCustomersFormat';
import type { CustomerListState } from './creditCustomersErrors';

/** La fila que pinta la tabla es la que entrega el BFF, sin recomponer nada. */
export type CreditCustomerRow = CreditCustomerListItem;

/** «Sin nivel» no es un nivel del motor: es la ausencia de uno, y se filtra aparte. */
export const NO_LEVEL = 'NO_LEVEL';

export type LevelFilterValue = ClientLevelEnum | typeof NO_LEVEL;

/** Los tres buckets de la barra de conteos. Se excluyen entre sí. */
export type CreditBucket = 'all' | 'active' | 'arrears';

/** Corta por los créditos VIGENTES del acreditado. Es el «Créditos vigentes» del mock. */
export type ActiveCreditsFilter = '' | '0' | '1+' | '2+' | '3+';

export interface CreditCustomersFilters {
    query: string;
    levels: LevelFilterValue[];
    status: CustomerCreditStatus | '';
    sciMin: string;
    sciMax: string;
    activeCredits: ActiveCreditsFilter;
    /** `segmentId` del segmento elegido. Vacío = todos. */
    segmentId: string;
    bucket: CreditBucket;
}

export const EMPTY_FILTERS: CreditCustomersFilters = {
    query: '',
    levels: [],
    status: '',
    sciMin: '',
    sciMax: '',
    activeCredits: '',
    segmentId: '',
    bucket: 'all',
};

export type SortKey =
    | 'id'
    | 'name'
    | 'level'
    | 'sci'
    | 'activeCredits'
    | 'balance'
    | 'status'
    | 'lastPayment';

export interface SortState {
    key: SortKey;
    direction: 'asc' | 'desc';
}

export const DEFAULT_SORT: SortState = { key: 'name', direction: 'asc' };

/** Click en el mismo encabezado invierte; en otro, arranca ascendente. */
export function nextSort(current: SortState, key: SortKey): SortState {
    if (current.key !== key) return { key, direction: 'asc' };
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

/** Agrega o quita un valor de una lista de chips multi-select. */
export function toggleInList<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

/** ¿Este filtro mira la cartera? Solo alcanza a las filas cuyo crédito el BFF pudo leer. */
export function usesCredit(filters: CreditCustomersFilters): boolean {
    return (
        filters.levels.length > 0 ||
        filters.status !== '' ||
        filters.sciMin !== '' ||
        filters.sciMax !== '' ||
        filters.activeCredits !== '' ||
        filters.bucket !== 'all'
    );
}

/** `warning` es el aviso que pide algo del analista; `neutral`, el que solo lo informa. */
export type FiltersNoteTone = 'warning' | 'neutral';

export interface FiltersAvailability {
    /** La búsqueda por nombre, teléfono o ID: sin lista no hay ni una fila que buscar. */
    searchOff: boolean;
    /** Los que miran la cartera. Con la lista leída se ofrecen: cada fila trae su propio estado. */
    creditOff: boolean;
    /** Por qué están apagados. `undefined` cuando el propio control ya lo comunica. */
    note: string | undefined;
    /** Con qué peso pintar la nota. `undefined` cuando no hay nota. */
    tone: FiltersNoteTone | undefined;
}

const LIST_STATE_TONE: Record<Exclude<CustomerListState, 'ready' | 'loading'>, FiltersNoteTone> = {
    error: 'warning',
    forbidden: 'warning',
};

/**
 * Qué controles se pueden ofrecer. Sin LISTA cae todo, la búsqueda incluida: con la lectura caída
 * no hay ni una fila sobre la que filtrar. Mientras carga los controles caen sin nota: eso ya lo
 * dicen la tabla y el propio campo deshabilitado.
 */
export function filtersAvailabilityOf(listState: CustomerListState): FiltersAvailability {
    if (listState === 'loading') {
        return { searchOff: true, creditOff: true, note: undefined, tone: undefined };
    }
    if (listState !== 'ready') {
        return {
            searchOff: true,
            creditOff: true,
            note: LIST_STATE_NOTE[listState],
            tone: LIST_STATE_TONE[listState],
        };
    }
    return { searchOff: false, creditOff: false, note: undefined, tone: undefined };
}

/** Cuántos filtros hay puestos — el contador del encabezado del panel. */
export function countActiveFilters(filters: CreditCustomersFilters): number {
    let count = 0;
    if (filters.query.trim() !== '') count += 1;
    count += filters.levels.length;
    if (filters.status !== '') count += 1;
    if (filters.sciMin !== '' || filters.sciMax !== '') count += 1;
    if (filters.activeCredits !== '') count += 1;
    if (filters.segmentId !== '') count += 1;
    if (filters.bucket !== 'all') count += 1;
    return count;
}

/** Lo tecleado es un número. Sin esto, el `2` de `rc-2` cazaría cualquier teléfono que lo tenga. */
const PHONE_QUERY = /^[\d\s()+-]+$/;

/** El teléfono se compara en dígitos: la celda lo muestra separado y así se copia. */
function matchesQuery(row: CreditCustomerRow, query: string): boolean {
    const haystack = `${row.fullName ?? ''} ${row.retailCustomerId}`.toLowerCase();
    if (haystack.includes(query)) return true;
    if (!PHONE_QUERY.test(query)) return false;
    const queryDigits = digitsOf(query);
    return queryDigits !== '' && digitsOf(row.phone ?? '').includes(queryDigits);
}

/**
 * ¿La fila cae bajo el segmento elegido? Con la lectura de segmentos fallada no se puede afirmar
 * pertenencia: la fila queda fuera en vez de colarse como si no perteneciera a ninguno.
 */
function matchesSegment(row: CreditCustomerRow, segmentId: string): boolean {
    if (segmentId === '') return true;
    if (!row.segmentsRead) return false;
    return row.segments.some((segment) => segment.segmentId === segmentId);
}

function matchesLevel(row: CreditCustomerRow, levels: LevelFilterValue[]): boolean {
    return levels.includes(row.credit.clientLevel ?? NO_LEVEL);
}

const ACTIVE_CREDITS_THRESHOLD: Record<Exclude<ActiveCreditsFilter, '' | '0'>, number> = {
    '1+': 1,
    '2+': 2,
    '3+': 3,
};

function matchesActiveCredits(row: CreditCustomerRow, filter: ActiveCreditsFilter): boolean {
    if (filter === '') return true;
    if (filter === '0') return row.credit.activeCreditsCount === 0;
    return row.credit.activeCreditsCount >= ACTIVE_CREDITS_THRESHOLD[filter];
}

/**
 * Con un rango de SCI puesto, el cliente SIN SCI queda fuera: no se puede afirmar que un SCI ausente
 * cae entre 40 y 60.
 */
function matchesSci(row: CreditCustomerRow, min: string, max: string): boolean {
    if (min === '' && max === '') return true;
    const sci = row.credit.sciScore;
    if (sci === null) return false;
    if (min !== '' && sci < Number(min)) return false;
    return !(max !== '' && sci > Number(max));
}

function matchesBucket(row: CreditCustomerRow, bucket: CreditBucket): boolean {
    if (bucket === 'active') return hasActiveCredit(row.credit);
    if (bucket === 'arrears') return isInArrears(row.credit);
    return true;
}

function matchesCredit(row: CreditCustomerRow, filters: CreditCustomersFilters): boolean {
    if (filters.levels.length > 0 && !matchesLevel(row, filters.levels)) return false;
    if (filters.status !== '' && creditRowStatusOf(row.credit) !== filters.status) return false;
    if (!matchesSci(row, filters.sciMin, filters.sciMax)) return false;
    if (!matchesActiveCredits(row, filters.activeCredits)) return false;
    return matchesBucket(row, filters.bucket);
}

/**
 * ¿Esta fila pasa los filtros? Los de cartera solo alcanzan a la fila cuyo crédito el BFF LEYÓ:
 * filtrar sobre un dato sin leer devolvería un resultado que miente sobre quién cumple.
 */
export function matchesFilters(
    row: CreditCustomerRow,
    filters: CreditCustomersFilters,
    query: string,
): boolean {
    if (query !== '' && !matchesQuery(row, query)) return false;
    if (!matchesSegment(row, filters.segmentId)) return false;
    if (!usesCredit(filters)) return true;
    return row.credit.state === 'READ' && matchesCredit(row, filters);
}

/** Aplica los filtros a una lista entera. La tabla usa el predicado; esto sirve a los conteos. */
export function filterCustomers(
    rows: readonly CreditCustomerRow[],
    filters: CreditCustomersFilters,
): CreditCustomerRow[] {
    const query = filters.query.trim().toLowerCase();
    return rows.filter((row) => matchesFilters(row, filters, query));
}

/** Sentinela de «sin dato»: ordena al final en ascendente, cualquiera sea la columna. */
const MISSING_NUMBER = Number.POSITIVE_INFINITY;
const MISSING_TEXT = '￿';

/** La columna de saldo ordena por la deuda total si el silo la publica; si no, por el capital. */
function balanceSortCentsOf(credit: CreditCustomerRow['credit']): number | null {
    return credit.totalDebtCents === undefined
        ? credit.outstandingBalanceCents
        : credit.totalDebtCents;
}

/** El valor por el que ordena cada columna. Lo sin dato cae al final en ascendente. */
export function sortValueOf(row: CreditCustomerRow, key: SortKey): number | string {
    const read = row.credit.state === 'READ';
    switch (key) {
        case 'id':
            return row.retailCustomerId.toLowerCase();
        case 'name':
            return row.fullName?.toLowerCase() ?? MISSING_TEXT;
        case 'level':
            if (!read || row.credit.clientLevel === null) return MISSING_NUMBER;
            return LEVEL_ORDER[row.credit.clientLevel];
        case 'sci':
            return read ? (row.credit.sciScore ?? MISSING_NUMBER) : MISSING_NUMBER;
        case 'activeCredits':
            return read ? row.credit.activeCreditsCount : MISSING_NUMBER;
        case 'balance':
            return read ? (balanceSortCentsOf(row.credit) ?? MISSING_NUMBER) : MISSING_NUMBER;
        case 'lastPayment':
            return read ? (row.credit.lastPaymentAt ?? MISSING_TEXT) : MISSING_TEXT;
        default:
            return read ? creditRowStatusSortValue(row.credit) : MISSING_NUMBER;
    }
}

/** Compara dos valores de orden. El texto va con el colador del español mexicano. */
export function compareSortValues(a: number | string, b: number | string): number {
    if (typeof a === 'string' || typeof b === 'string') {
        return String(a).localeCompare(String(b), 'es-MX');
    }
    return a === b ? 0 : a < b ? -1 : 1;
}

/**
 * Ordena una copia de las filas. Lo que no tiene dato cae SIEMPRE al final en ascendente — un hueco
 * arriba de todo se lee como el mejor valor de la columna.
 */
export function sortCustomers(
    rows: readonly CreditCustomerRow[],
    sort: SortState,
): CreditCustomerRow[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort(
        (a, b) => compareSortValues(sortValueOf(a, sort.key), sortValueOf(b, sort.key)) * sign,
    );
}

/**
 * Los segmentos que el filtro puede ofrecer: los que traen las filas CARGADAS, sin repetir y en
 * orden alfabético. No es el catálogo del tenant — un segmento sin ningún miembro cargado no está.
 */
export function segmentOptionsOf(
    rows: readonly CreditCustomerRow[],
): { segmentId: string; name: string }[] {
    const byId = new Map<string, { segmentId: string; name: string }>();
    for (const row of rows) {
        if (!row.segmentsRead) continue;
        for (const segment of row.segments) byId.set(segment.segmentId, segment);
    }
    return [...byId.values()].sort((left, right) => left.name.localeCompare(right.name, 'es-MX'));
}

/** ¿El BFF pudo leer los segmentos de alguna fila? Sin ninguna, filtrar por segmento no afirma nada. */
export function anySegmentsRead(rows: readonly CreditCustomerRow[]): boolean {
    return rows.some((row) => row.segmentsRead);
}
